import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import styles from './TimePicker.module.css';

// --- Helper Function (defined outside the component to avoid re-creation) ---
function debounce(func, delay) {
    let timeout;
    return function(...args) {
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(this, args), delay);
    };
}

// --- Dropdown Portal Component (sem alterações) ---
const TimePickerDropdown = ({
    isOpen,
    targetRef,
    onClose,
    children
}) => {
    const dropdownRef = useRef(null);
    const [style, setStyle] = useState({});

    // Calculate position when opened
    useEffect(() => {
        if (isOpen && targetRef.current && dropdownRef.current) {
            const inputRect = targetRef.current.getBoundingClientRect();
            const dropdownHeight = dropdownRef.current.offsetHeight;
            const spaceBelow = window.innerHeight - inputRect.bottom;

            let top;
            // If not enough space below, and more space above, open upwards
            if (spaceBelow < dropdownHeight && inputRect.top > dropdownHeight) {
                top = inputRect.top - dropdownHeight - 8;
            } else {
                // Otherwise, open downwards
                top = inputRect.bottom + 8;
            }

            setStyle({
                position: 'absolute',
                top: `${top}px`,
                left: `${inputRect.left}px`,
                width: `${inputRect.width}px`,
            });
        }
    }, [isOpen, targetRef]);

    // Handle clicks outside to close
    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
                targetRef.current && !targetRef.current.contains(event.target)) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen, onClose, targetRef]);

    if (!isOpen) return null;

    return ReactDOM.createPortal(
        <div ref={dropdownRef} className={`${styles.dropdown} ${isOpen ? styles.show : ''}`} style={style}>
            {children}
        </div>,
        document.body
    );
};


// --- The Main TimePicker Component (com alterações) ---
export const ElegantTimePicker = ({ 
    name,
    value = '00:00',
    minuteInterval = 1,
    onChange = () => {}
}) => {
    const [isOpen, setIsOpen] = useState(false);
    
    const [selectedHour, setSelectedHour] = useState('00');
    const [selectedMinute, setSelectedMinute] = useState('00');

    const containerRef = useRef(null);
    const hourColumnRef = useRef(null);
    const minuteColumnRef = useRef(null);
    const hourListRef = useRef(null);
    const minuteListRef = useRef(null);
    
    const itemHeight = 42;

    useEffect(() => {
        const [hour, minute] = value.split(':');
        if (hour && minute) {
            setSelectedHour(hour);
            setSelectedMinute(minute);
        }
    }, [value]);

    const handleSnap = useCallback((column, list, isHour) => {
        if (!column || !list) return;
        const roundedIndex = Math.round(column.scrollTop / itemHeight);
        const totalItemsInSet = isHour ? 24 : 60 / minuteInterval;
        const selectedItemIndex = roundedIndex % totalItemsInSet;
        const selectedItem = list.querySelectorAll(`.${styles.item}`)[selectedItemIndex];

        if (selectedItem) {
            const newValue = selectedItem.dataset.value;
            const newTimeValue = isHour 
                ? `${newValue}:${selectedMinute}` 
                : `${selectedHour}:${newValue}`;

            const syntheticEvent = {
                target: { name, value: newTimeValue }
            };
            onChange(syntheticEvent);
        }
    }, [minuteInterval, name, onChange, selectedHour, selectedMinute]);

    // NOVO: Função para lidar com o clique em um item (hora ou minuto)
    const handleItemClick = useCallback((event, isHour) => {
        const item = event.target.closest(`.${styles.item}`);
        if (!item) return; // Se o clique não foi em um item, ignora

        const newValue = item.dataset.value;
        
        // Evita chamadas desnecessárias se o item clicado já estiver selecionado
        if ((isHour && newValue === selectedHour) || (!isHour && newValue === selectedMinute)) {
            return;
        }

        const newTimeValue = isHour
            ? `${newValue}:${selectedMinute}`
            : `${selectedHour}:${newValue}`;

        const syntheticEvent = {
            target: { name, value: newTimeValue }
        };
        onChange(syntheticEvent);
        // Não precisamos rolar manualmente. A atualização do estado (via prop 'value')
        // fará com que o useEffect de rolagem seja acionado, centralizando o novo valor.
    }, [onChange, name, selectedHour, selectedMinute]);


    useEffect(() => {
        if (isOpen) {
            const generateTimeSlots = (max, interval) => {
                let content = '';
                for (let i = 0; i < max; i += interval) {
                    const valueStr = i.toString().padStart(2, '0');
                    content += `<li class="${styles.item}" data-value="${valueStr}">${valueStr}</li>`;
                }
                return content;
            };

            const setupInfiniteScroll = (list, max, interval) => {
                const originalContent = generateTimeSlots(max, interval);
                const cloneCount = 5;
                if (list) {
                    list.innerHTML = Array(cloneCount).fill(originalContent).join('');
                }
            };
            
            setupInfiniteScroll(hourListRef.current, 24, 1);
            setupInfiniteScroll(minuteListRef.current, 60, minuteInterval);
        }
    }, [isOpen, minuteInterval]);

    useEffect(() => {
        const hourCol = hourColumnRef.current;
        const minCol = minuteColumnRef.current;
        const hourList = hourListRef.current; // NOVO: Referência à lista de horas
        const minuteList = minuteListRef.current; // NOVO: Referência à lista de minutos

        if (isOpen && hourCol && minCol && hourList && minuteList) {
            const debouncedHourSnap = debounce(() => handleSnap(hourCol, hourList, true), 200);
            const debouncedMinuteSnap = debounce(() => handleSnap(minCol, minuteList, false), 200);

            // NOVO: Manipuladores de clique específicos para cada coluna
            const onHourClick = (e) => handleItemClick(e, true);
            const onMinuteClick = (e) => handleItemClick(e, false);

            hourCol.addEventListener('scroll', debouncedHourSnap);
            minCol.addEventListener('scroll', debouncedMinuteSnap);
            // NOVO: Adiciona os listeners de clique às listas
            hourList.addEventListener('click', onHourClick);
            minuteList.addEventListener('click', onMinuteClick);

            return () => {
                hourCol.removeEventListener('scroll', debouncedHourSnap);
                minCol.removeEventListener('scroll', debouncedMinuteSnap);
                // NOVO: Remove os listeners de clique na limpeza para evitar memory leaks
                hourList.removeEventListener('click', onHourClick);
                minuteList.removeEventListener('click', onMinuteClick);
            };
        }
    }, [isOpen, handleSnap, handleItemClick]); // NOVO: Adiciona handleItemClick às dependências

    useEffect(() => {
        const updateSelectedVisuals = (list, val) => {
            if (!list) return;
            list.querySelectorAll(`.${styles.item}`).forEach(item => {
                item.classList.toggle(styles.selected, item.dataset.value === val);
            });
        };
        
        const scrollToInitialValue = (column, list, val, totalItemsInSet) => {
            if (!column || !list) return;
            const cloneCount = 5;
            const middleSetIndex = Math.floor(cloneCount / 2);
            const valueIndex = parseInt(val, 10) / (list === hourListRef.current ? 1 : minuteInterval);
            
            const targetIndex = (middleSetIndex * totalItemsInSet) + valueIndex;
            const initialScrollTop = targetIndex * itemHeight;

            // Usar 'smooth' para o scroll pode ser uma boa experiência, mas para o clique
            // a rolagem instantânea via estado é melhor. Mantemos 'auto' aqui.
            column.scrollTo({ top: initialScrollTop, behavior: 'auto' });
            updateSelectedVisuals(list, val);
        };
        
        if (isOpen) {
            scrollToInitialValue(hourColumnRef.current, hourListRef.current, selectedHour, 24);
            scrollToInitialValue(minuteColumnRef.current, minuteListRef.current, selectedMinute, 60 / minuteInterval);
        }
    }, [isOpen, selectedHour, selectedMinute, minuteInterval]);

    return (
        <div className={`${styles.container} ${isOpen ? styles.active : ''}`} ref={containerRef}>
            <div className={styles.inputWrapper} onClick={() => setIsOpen(prev => !prev)}>
                <input
                    type="text"
                    className={styles.input}
                    placeholder="Selecione um horário..."
                    readOnly
                    value={value}
                />
                <div className={styles.icon}>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M10 19C14.9706 19 19 14.9706 19 10C19 5.02944 14.9706 1 10 1C5.02944 1 1 5.02944 1 10C1 14.9706 5.02944 19 10 19Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M10 5.5V10.5L13 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </div>
            </div>
            <TimePickerDropdown isOpen={isOpen} targetRef={containerRef} onClose={() => setIsOpen(false)}>
                <div className={styles.column} ref={hourColumnRef}>
                    <ul className={styles.list} ref={hourListRef}></ul>
                </div>
                <div className={styles.column} ref={minuteColumnRef}>
                    <ul className={styles.list} ref={minuteListRef}></ul>
                </div>
            </TimePickerDropdown>
        </div>
    );
};